import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Skeleton,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import RequestTimeOffModal from '../components/RequestTimeOffModal';
import dayjs from 'dayjs';
import { getUserVacations, getVacationDays, deleteVacation } from '../api/services';

// ---------------------------------------------------------------
// Design tokens + presentational helpers (no business logic)
// ---------------------------------------------------------------
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  success: '#2e7d32',
  successSoft: '#e8f5e9',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  warning: '#b26a00',
  warningSoft: '#fff4e5',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const chipTones = {
  success: { bg: ui.successSoft, color: ui.success },
  danger: { bg: ui.dangerSoft, color: ui.danger },
  warning: { bg: ui.warningSoft, color: ui.warning },
  neutral: { bg: '#eef1f4', color: '#4b5563' },
};

const statusTone = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'success';
  if (s === 'pending' || s === 'submitted') return 'warning';
  if (s === 'denied' || s === 'rejected') return 'danger';
  return 'neutral';
};

const statusLabel = (status) => {
  const s = String(status || '');
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
};

const formatDays = (days) => `${days} day${days === 1 ? '' : 's'}`;

const StatusChip = ({ status }) => {
  const t = chipTones[statusTone(status)];
  return (
    <Chip
      size="small"
      label={statusLabel(status)}
      sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: t.bg, color: t.color }}
    />
  );
};

// Ring showing the share of the allowance that is still unused
const BalanceRing = ({ unused, total }) => {
  const size = 156;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const share = total > 0 ? Math.max(0, Math.min(1, unused / total)) : 0;

  return (
    <Box
      role="img"
      aria-label={`${unused} of ${total} vacation days unused`}
      sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#fff"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - share)}
          style={{ transition: 'stroke-dashoffset 0.9s ease' }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '2.4rem', lineHeight: 1 }}>{unused}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
          Unused
        </Typography>
      </Box>
    </Box>
  );
};

const HeroStat = ({ label, value, icon: Icon }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      borderRadius: '14px',
      backgroundColor: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.2)',
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 38,
        height: 38,
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.2)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon fontSize="small" />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2, fontSize: '1.15rem' }}>
        {value}{' '}
        <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
          Days
        </Typography>
      </Typography>
    </Box>
  </Box>
);

const SectionCard = ({ title, children }) => (
  <Box
    sx={{
      mb: { xs: 2.5, md: 4 },
      borderRadius: '20px',
      border: `1px solid ${ui.border}`,
      backgroundColor: '#fff',
      boxShadow: ui.shadow,
      p: { xs: 2, sm: 3, md: 4 },
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: ui.text,
        pl: 1.5,
        borderLeft: `4px solid ${ui.primary}`,
        lineHeight: 1.3,
        mb: 3,
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const EmptyState = ({ icon: Icon, title }) => (
  <Box sx={{ textAlign: 'center', py: { xs: 4, md: 5 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: 'auto',
        mb: 2,
        borderRadius: '50%',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 32 }} />
    </Box>
    <Typography sx={{ fontWeight: 700, color: ui.text }}>{title}</Typography>
  </Box>
);

const ListSkeleton = ({ rows = 3 }) => (
  <Box role="status" aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <Skeleton key={i} variant="rounded" height={84} animation="wave" />
    ))}
  </Box>
);

// Card for an upcoming vacation (calendar tile + details + delete)
const UpcomingCard = ({ vacation, onDelete }) => {
  const [start, end] = vacation.dateRange.split(' - ');
  const tone = chipTones[statusTone(vacation.status)];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        minWidth: 0,
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        borderLeft: `5px solid ${tone.color}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(16, 24, 40, 0.10)' },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 58,
          height: 66,
          borderRadius: '14px',
          backgroundColor: ui.primarySoft,
          color: ui.primaryDark,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: 1, lineHeight: 1 }}>
          {dayjs(start).format('MMM').toUpperCase()}
        </Typography>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.1 }}>
          {dayjs(start).format('D')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: ui.text }}>
          {dayjs(start).format('MMM D')} – {dayjs(end).format('MMM D, YYYY')}
        </Typography>
        <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word', mb: 1 }}>
          {vacation.type}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={formatDays(vacation.days)}
            sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: ui.primarySoft, color: ui.primaryDark }}
          />
          <StatusChip status={vacation.status} />
        </Box>
      </Box>

      <Tooltip title="Delete">
        <IconButton
          onClick={() => onDelete(vacation.id)}
          sx={{ color: ui.danger, '&:hover': { backgroundColor: ui.dangerSoft } }}
          aria-label="delete vacation"
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Timeline entry for the history list
const HistoryItem = ({ vacation, last }) => {
  const [start, end] = vacation.dateRange.split(' - ');
  const tone = chipTones[statusTone(vacation.status)];

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '28px minmax(0, 1fr)',
        columnGap: 1.5,
        pb: last ? 0 : 2.5,
        '&::before': last
          ? { display: 'none' }
          : {
              content: '""',
              position: 'absolute',
              left: 9,
              top: 22,
              bottom: 0,
              width: 2,
              backgroundColor: ui.border,
            },
      }}
    >
      <Box
        sx={{
          mt: 0.75,
          width: 20,
          height: 20,
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: `4px solid ${tone.color}`,
          boxSizing: 'border-box',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          borderRadius: '14px',
          backgroundColor: ui.surface,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: ui.text }}>
            {dayjs(start).format('MMM D')} – {dayjs(end).format('MMM D, YYYY')}
          </Typography>
          <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word' }}>
            {vacation.type}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: ui.text, fontWeight: 600 }}>
            {formatDays(vacation.days)}
          </Typography>
          <StatusChip status={vacation.status} />
        </Box>
      </Box>
    </Box>
  );
};

const Vacation = () => {
  const [open, setOpen] = useState(false);
  const [statusAndHistory, setStatusAndHistory] = useState([]);
  const [nextScheduledVacation, setNextScheduledVacation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vacationSummary, setVacationSummary] = useState({
    unusedVacation: 0,
    usedVacation: 0,
    totalVacation: 0,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setSnackbarOpen(false);

  const fetchVacations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const vacations = await getUserVacations(0, 100);
      const vacationArray = Array.isArray(vacations) ? vacations : [];

      const formattedVacations = vacationArray
        .filter((vacation) => {
          const isValid =
            vacation.id &&
            vacation.startDate &&
            vacation.endDate &&
            vacation.status &&
            dayjs(vacation.startDate).isValid() &&
            dayjs(vacation.endDate).isValid();
          if (!isValid) return false;
          const days = dayjs(vacation.endDate).diff(vacation.startDate, 'day') + 1;
          return days > 0;
        })
        .map((vacation) => ({
          id: vacation.id,
          dateRange: `${dayjs(vacation.startDate).format('YYYY-MM-DD')} - ${dayjs(vacation.endDate).format('YYYY-MM-DD')}`,
          days: dayjs(vacation.endDate).diff(vacation.startDate, 'day') + 1,
          type: vacation.reason || 'Vacation',
          status: vacation.status || 'Pending',
        }));

      setStatusAndHistory(formattedVacations);

      const today = dayjs().startOf('day');
      const scheduledVacations = formattedVacations.filter((vacation) => {
        const [, endDate] = vacation.dateRange.split(' - ');
        const end = dayjs(endDate).startOf('day');
        const isFutureOrOngoing = end.isSame(today, 'day') || end.isAfter(today);
        const normalizedStatus = vacation.status.toLowerCase();
        const isPendingOrApproved =
          normalizedStatus === 'pending' ||
          normalizedStatus === 'approved' ||
          normalizedStatus === 'submitted';
        return isFutureOrOngoing && isPendingOrApproved;
      });

      setNextScheduledVacation(scheduledVacations);
    } catch (err) {
      setError(`Failed to fetch vacation history: ${err.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVacationDays = useCallback(async () => {
    try {
      const data = await getVacationDays();
      setVacationSummary({
        unusedVacation: data.unusedDays || 0,
        usedVacation: data.usedDays || 0,
        totalVacation: data.totalDays || 0,
      });
    } catch (err) {
      setError(`Failed to fetch vacation days summary: ${err.message}. Please try again.`);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    await Promise.all([fetchVacations(), fetchVacationDays()]);
  }, [fetchVacations, fetchVacationDays]);

  const handleDeleteVacation = async (id) => {
    try {
      await deleteVacation(id);
      setSnackbarMessage('Vacation deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      await fetchAllData();
    } catch (err) {
      setSnackbarMessage(`Failed to delete vacation: ${err.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleVacationRequestSubmit = async () => {
    try {
      await fetchAllData();
    } catch (err) {
      setError(`Failed to refresh vacation data: ${err.message}. Please try again.`);
    }
  };

  useEffect(() => {
    fetchAllData();
    const intervalId = setInterval(() => {
      fetchAllData();
    }, 600000);
    return () => clearInterval(intervalId);
  }, [fetchAllData]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        pt: { xs: '110px', sm: '120px', md: '130px' },
        pb: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 4, md: 8 },
      }}
    >
      <Box sx={{ maxWidth: '1200px', margin: '0 auto', mt: 2 }}>
        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            mb: { xs: 2.5, md: 4 },
            borderRadius: '24px',
            color: '#fff',
            background: `linear-gradient(135deg, ${ui.primaryDark} 0%, ${ui.primary} 55%, #2bbbd4 100%)`,
            boxShadow: '0 12px 32px rgba(0, 122, 138, 0.28)',
            p: { xs: 3, sm: 4, md: 5 },
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 320,
              height: 320,
              borderRadius: '50%',
              top: -140,
              right: -80,
              background: 'rgba(255,255,255,0.10)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 220,
              height: 220,
              borderRadius: '50%',
              bottom: -110,
              left: '35%',
              background: 'rgba(255,255,255,0.07)',
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 3, md: 5 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  mb: 2,
                  borderRadius: '14px',
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FlightTakeoffIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', sm: '2.1rem' }, lineHeight: 1.2, mb: 3 }}
              >
                Vacation Management
              </Typography>
              <Button
                variant="contained"
                onClick={handleOpen}
                data-tour="vac-request-button"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: '#fff',
                  color: ui.primaryDark,
                  fontWeight: 700,
                  py: 1.25,
                  px: 3,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                  '&:hover': { backgroundColor: '#f1fbfd', boxShadow: '0 8px 20px rgba(0,0,0,0.22)' },
                }}
              >
                Request Time Off
              </Button>
            </Box>

            <Box
              data-tour="vac-stats"
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2.5, sm: 3 },
                width: { xs: '100%', md: 'auto' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <BalanceRing unused={vacationSummary.unusedVacation} total={vacationSummary.totalVacation} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minWidth: { sm: 200 } }}>
                <HeroStat label="Unused Vacation" value={vacationSummary.unusedVacation} icon={EventAvailableOutlinedIcon} />
                <HeroStat label="Used Vacation" value={vacationSummary.usedVacation} icon={EventBusyOutlinedIcon} />
                <HeroStat label="Total Vacation" value={vacationSummary.totalVacation} icon={DateRangeOutlinedIcon} />
              </Box>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {/* Next scheduled vacation */}
        <SectionCard title="Next Scheduled Vacation">
          {loading ? (
            <ListSkeleton rows={2} />
          ) : nextScheduledVacation.length === 0 ? (
            <EmptyState icon={EventNoteOutlinedIcon} title="No scheduled vacations." />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              {nextScheduledVacation.map((vacation) => (
                <UpcomingCard key={vacation.id} vacation={vacation} onDelete={handleDeleteVacation} />
              ))}
            </Box>
          )}
        </SectionCard>

        {/* Status and history */}
        <SectionCard title="Status and History">
          {loading ? (
            <ListSkeleton rows={3} />
          ) : statusAndHistory.length === 0 ? (
            <EmptyState icon={HistoryOutlinedIcon} title="No previous vacation history." />
          ) : (
            <Box data-tour="vac-history">
              {statusAndHistory.map((vacation, index) => (
                <HistoryItem
                  key={vacation.id}
                  vacation={vacation}
                  last={index === statusAndHistory.length - 1}
                />
              ))}
            </Box>
          )}
        </SectionCard>
      </Box>

      <RequestTimeOffModal open={open} onClose={handleClose} onSubmit={handleVacationRequestSubmit} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Vacation;